import React, { useState } from "react";
import { useTranslation } from 'react-i18next';
import { useOrganizationStatus } from '../../hooks/useOrganizationStatus';
import QuotaExceededModal from '../common/QuotaExceededModal';
import ReadOnlyModal from '../common/ReadOnlyModal';
import { getPlanLimits } from '../../Config/plans';
import { fabricTemplates, TemplateLevel } from '../../Config/fabric/fabricTemplates';
import MultiDomainWizardModal from './MultiDomainWizardModal';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (architectureData: {
    name: string;
    description: string;
    type: string;
    deploymentType: string;
    template?: string;
    diagramData?: { nodes: any[]; edges: any[] };
  }) => void;
  projectName?: string;
  provider?: string;
  quotaError?: {
    current_usage: number;
    quota_limit: number;
    upgrade_suggestion?: {
      target: string;
      message: string;
      price: string;
    };
  } | null;
};

const LEVEL_COLORS: Record<TemplateLevel, string> = {
  'Starter':        '#166534',
  'Intermédiaire':  '#1E40AF',
  'Production':     '#92400E',
  'Enterprise':     '#6B21A8',
};

const CreateArchitectureModal: React.FC<Props> = ({ isOpen, onClose, onCreate, projectName, provider, quotaError }) => {
  const isFabric = (provider || '').toLowerCase() === 'fabric';
  const { t } = useTranslation('architecture');
  const { organization, subscription, usage } = useOrganizationStatus();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [type, setType] = useState("Web Application");
  const [deploymentType, setDeploymentType] = useState("Mono-cloud");
  const [useTemplate, setUseTemplate] = useState(false);
  const [template, setTemplate] = useState("blank");
  const [showQuotaModal, setShowQuotaModal] = useState(false);
  const [showReadOnlyModal, setShowReadOnlyModal] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [wizardDiagramData, setWizardDiagramData] = useState<{ nodes: any[]; edges: any[] } | null>(null);

  // Afficher le modal de quota dépassé si quotaError est fourni
  React.useEffect(() => {
    if (quotaError) {
      setShowQuotaModal(true);
    }
  }, [quotaError]);

  if (!isOpen) return null;

  const handleSubmit = () => {
    if (!name.trim()) return; // Validation - le bouton sera disabled si vide

    // Vérifier le statut de l'organisation
    if (organization?.status) {
      const normalizedStatus = organization.status.toUpperCase().replace(/-/g, '_');

      if (['SUSPENDED', 'CANCELLED', 'TRIAL_EXPIRED'].includes(normalizedStatus)) {
        setShowReadOnlyModal(true);
        return;
      }

      // OVER_QUOTA peut être dû au quota projets, pas architectures.
      // On vérifie uniquement le quota architectures pour bloquer la création.
      if (normalizedStatus === 'OVER_QUOTA') {
        const archLimit = getPlanLimits(subscription?.plan).architectures;
        const archUsage = usage?.architectures ?? 0;
        if (archLimit !== -1 && archUsage >= archLimit) {
          setShowQuotaModal(true);
          return;
        }
        // Quota dépassé sur autre ressource (ex: projets) → pas de blocage ici
      }
    }

    onCreate({
      name,
      description,
      type,
      deploymentType,
      template: (useTemplate && template !== 'wizard') ? template : undefined,
      diagramData: (useTemplate && template === 'wizard' && wizardDiagramData) ? wizardDiagramData : undefined,
    });
    onClose();
    setName("");
    setDescription("");
    setType("Web Application");
    setDeploymentType("Mono-cloud");
    setUseTemplate(false);
    setTemplate("blank");
    setWizardDiagramData(null);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white text-gray-900 p-8 rounded-xl shadow-2xl w-[80%] max-w-3xl">
        <h2 className="text-2xl font-bold mb-6 text-gray-900 border-b border-gray-200 pb-3">
          {t('modals.createArchitecture.title')}
        </h2>

        {projectName && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <span className="text-sm text-blue-900">
              <strong>{t('modals.createArchitecture.projectLabel')}</strong> {projectName}
            </span>
          </div>
        )}

        <div className="grid grid-cols-2 gap-6">
          {/* Nom */}
          <div className="col-span-2">
            <label className="block text-sm mb-2 font-medium text-gray-700">{t('modals.createArchitecture.fields.name.label')}</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-white text-gray-900 border border-gray-300 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t('modals.createArchitecture.fields.name.placeholder')}
            />
          </div>

          {/* Description */}
          <div className="col-span-2">
            <label className="block text-sm mb-2 font-medium text-gray-700">{t('modals.createArchitecture.fields.description.label')}</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full bg-white text-gray-900 border border-gray-300 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t('modals.createArchitecture.fields.description.placeholder')}
            />
          </div>

          {/* Type d'architecture */}
          <div>
            <label className="block text-sm mb-2 font-medium text-gray-700">{t('modals.createArchitecture.fields.type.label')}</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="w-full bg-white text-gray-900 border border-gray-300 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="Web Application">{t('modals.createArchitecture.fields.type.options.webApp')}</option>
              <option value="Microservices">{t('modals.createArchitecture.fields.type.options.microservices')}</option>
              <option value="Data Processing">{t('modals.createArchitecture.fields.type.options.dataProcessing')}</option>
              <option value="IoT">{t('modals.createArchitecture.fields.type.options.iot')}</option>
              <option value="Machine Learning">{t('modals.createArchitecture.fields.type.options.machineLearning')}</option>
              <option value="Serverless">{t('modals.createArchitecture.fields.type.options.serverless')}</option>
              <option value="Container Orchestration">{t('modals.createArchitecture.fields.type.options.containerOrchestration')}</option>
              <option value="Autre">{t('modals.createArchitecture.fields.type.options.other')}</option>
            </select>
          </div>

          {/* Type de déploiement */}
          <div>
            <label className="block text-sm mb-2 font-medium text-gray-700">{t('modals.createArchitecture.fields.deploymentType.label')}</label>
            <select
              value={deploymentType}
              onChange={(e) => setDeploymentType(e.target.value)}
              className="w-full bg-white text-gray-900 border border-gray-300 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="Mono-cloud">{t('modals.createArchitecture.fields.deploymentType.options.monoCloud')}</option>
              <option value="Multi-cloud">{t('modals.createArchitecture.fields.deploymentType.options.multiCloud')}</option>
              <option value="Hybride">{t('modals.createArchitecture.fields.deploymentType.options.hybrid')}</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {t('modals.createArchitecture.fields.deploymentType.hint')}
            </p>
          </div>

          {/* Template */}
          <div className="col-span-2">
            <label className="block text-sm mb-3 font-medium text-gray-700 flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={useTemplate}
                onChange={(e) => {
                  setUseTemplate(e.target.checked);
                  setTemplate('blank');
                }}
                className="mr-1"
              />
              {t('modals.createArchitecture.fields.template.label')}
            </label>

            {/* Fabric templates — card picker */}
            {isFabric && useTemplate && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {fabricTemplates.map(tpl => (
                  <div
                    key={tpl.id}
                    onClick={() => { setTemplate(tpl.id); setWizardDiagramData(null); }}
                    style={{
                      border: `2px solid ${template === tpl.id ? '#038387' : '#E5E7EB'}`,
                      borderRadius: 8,
                      padding: '10px 12px',
                      cursor: 'pointer',
                      background: template === tpl.id ? '#F0FDFE' : 'white',
                      transition: 'all 0.15s',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ fontSize: 18 }}>{tpl.icon}</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#111827', flex: 1 }}>{tpl.name}</span>
                      <span style={{
                        fontSize: 10, fontWeight: 600,
                        padding: '1px 6px', borderRadius: 10,
                        color: LEVEL_COLORS[tpl.level],
                        background: `${LEVEL_COLORS[tpl.level]}15`,
                        border: `1px solid ${LEVEL_COLORS[tpl.level]}40`,
                        whiteSpace: 'nowrap',
                      }}>{tpl.level}</span>
                    </div>
                    <p style={{ fontSize: 11, color: '#6B7280', margin: 0, lineHeight: 1.4 }}>
                      {tpl.nodes.length} ressources · {tpl.edges.length} connexions
                    </p>
                  </div>
                ))}

                {/* Wizard Multi-Domain card */}
                <div
                  onClick={() => setShowWizard(true)}
                  style={{
                    border: `2px solid ${template === 'wizard' ? '#7C3AED' : '#E5E7EB'}`,
                    borderRadius: 8, padding: '10px 12px', cursor: 'pointer',
                    background: template === 'wizard' ? '#F5F3FF' : 'white',
                    transition: 'all 0.15s', gridColumn: 'span 2',
                    display: 'flex', alignItems: 'center', gap: 12,
                  }}
                >
                  <span style={{ fontSize: 24 }}>🧩</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#7C3AED', marginBottom: 2 }}>
                      Configurer Multi-Domain personnalisé…
                    </div>
                    <div style={{ fontSize: 11, color: '#6B7280' }}>
                      {template === 'wizard' && wizardDiagramData
                        ? `✓ ${wizardDiagramData.nodes.filter((n: any) => n.data?.type === 'fabric_domain').length} domaines · ${wizardDiagramData.nodes.filter((n: any) => n.data?.type === 'fabric_workspace').length} workspaces · ${wizardDiagramData.nodes.filter((n: any) => n.type === 'custom').length} items — cliquez pour modifier`
                        : 'Wizard 3 étapes · définissez vos domaines, workspaces et items'
                      }
                    </div>
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: '#EDE9FE', color: '#7C3AED' }}>
                    Personnalisé
                  </span>
                </div>
              </div>
            )}

            {/* Non-Fabric templates — standard dropdown */}
            {!isFabric && (
              <select
                value={template}
                onChange={(e) => setTemplate(e.target.value)}
                disabled={!useTemplate}
                className="w-full bg-white text-gray-900 border border-gray-300 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="blank">{t('modals.createArchitecture.fields.template.options.blank')}</option>
                <option value="3tier">{t('modals.createArchitecture.fields.template.options.tier3')}</option>
                <option value="microservices">{t('modals.createArchitecture.fields.template.options.microservicesK8s')}</option>
                <option value="serverless">{t('modals.createArchitecture.fields.template.options.serverlessLambda')}</option>
                <option value="data-pipeline">{t('modals.createArchitecture.fields.template.options.dataPipeline')}</option>
              </select>
            )}
          </div>
        </div>

        {/* Boutons */}
        <div className="flex justify-end gap-3 mt-8">
          <button
            onClick={onClose}
            className="px-6 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition"
          >
            {t('modals.createArchitecture.buttons.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={!name.trim()}
            className="px-6 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-600"
          >
            {t('modals.createArchitecture.buttons.create')}
          </button>
        </div>
      </div>

      {/* Wizard Multi-Domain */}
      <MultiDomainWizardModal
        isOpen={showWizard}
        onClose={() => setShowWizard(false)}
        onComplete={({ diagramData }) => {
          setWizardDiagramData(diagramData);
          setTemplate('wizard');
          setShowWizard(false);
        }}
      />

      {/* Modal de quota dépassé */}
      <QuotaExceededModal
        isOpen={showQuotaModal}
        onClose={() => setShowQuotaModal(false)}
        resourceType="architecture"
        currentUsage={quotaError?.current_usage || usage?.architectures || 0}
        quotaLimit={quotaError?.quota_limit || getPlanLimits(subscription?.plan).architectures}
        plan={subscription?.plan || 'free'}
        upgradeSuggestion={quotaError?.upgrade_suggestion}
      />

      {/* Modal READ_ONLY */}
      <ReadOnlyModal
        isOpen={showReadOnlyModal}
        onClose={() => setShowReadOnlyModal(false)}
        resourceType="architecture"
      />
    </div>
  );
};

export default CreateArchitectureModal;
